import { useCallback, useEffect, useRef, useState } from "react";
import type { Theme } from "@excalidraw/excalidraw/element/types";
import { applyThemeTokens } from "@excalidraw-ms/shared-ui";
import {
  API_PATHS,
  type ThemePreference,
  type UpdateUserProfileRequest,
  type UserProfileResponse,
} from "@excalidraw-ms/shared";
import { BACKEND_API_BASE } from "../env";

export type { ThemePreference } from "@excalidraw-ms/shared";

const SYSTEM_THEME_QUERY = "(prefers-color-scheme: dark)";
const STORAGE_KEY_PREFIX = "excalidraw-ms:theme:";
export const LAST_THEME_STORAGE_KEY = "excalidraw-ms:theme:last-used";
export const LAST_THEME_COOKIE = "excalidraw-ms-theme";

function isThemePreference(value: string | null): value is ThemePreference {
  return value === "light" || value === "dark" || value === "system";
}

function systemTheme(): Theme {
  return window.matchMedia?.(SYSTEM_THEME_QUERY).matches ? "dark" : "light";
}

function cookiePreference(): ThemePreference | null {
  const encodedPrefix = `${LAST_THEME_COOKIE}=`;
  const value = document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(encodedPrefix))
    ?.slice(encodedPrefix.length);
  const candidate = value ?? null;
  return isThemePreference(candidate) ? candidate : null;
}

function persistDevicePreference(preference: ThemePreference): void {
  window.localStorage.setItem(LAST_THEME_STORAGE_KEY, preference);
  document.cookie = `${LAST_THEME_COOKIE}=${preference}; Path=/; Max-Age=31536000; SameSite=Lax`;
}

export function themePreferenceStorageKey(accountId: string): string {
  return `${STORAGE_KEY_PREFIX}${accountId}`;
}

function storedPreference(accountId: string | undefined): ThemePreference {
  if (!accountId) {
    const lastTheme = window.localStorage.getItem(LAST_THEME_STORAGE_KEY);
    if (isThemePreference(lastTheme)) return lastTheme;
    const cookieTheme = cookiePreference();
    if (cookieTheme) {
      window.localStorage.setItem(LAST_THEME_STORAGE_KEY, cookieTheme);
      return cookieTheme;
    }

    // Migrate installations that selected a theme before the signed-out mirror
    // existed. A single account-scoped value is unambiguous; multiple cached
    // accounts deliberately fall back to System rather than guessing a user.
    const legacyAccountPreferences: ThemePreference[] = [];
    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = window.localStorage.key(index);
      if (!key?.startsWith(STORAGE_KEY_PREFIX) || key === LAST_THEME_STORAGE_KEY) continue;
      const value = window.localStorage.getItem(key);
      if (isThemePreference(value)) legacyAccountPreferences.push(value);
    }
    if (legacyAccountPreferences.length === 1) {
      persistDevicePreference(legacyAccountPreferences[0]);
      return legacyAccountPreferences[0];
    }
    return "system";
  }

  const value = window.localStorage.getItem(themePreferenceStorageKey(accountId));
  return isThemePreference(value) ? value : "system";
}

export function applyInitialTheme(): void {
  const preference = storedPreference(undefined);
  applyThemeTokens(preference === "system" ? systemTheme() : preference, document.documentElement);
}

interface ThemePreferenceOptions {
  accountId: string | undefined;
  getToken?: () => Promise<string>;
  syncEnabled?: boolean;
}

export function useThemePreference({
  accountId,
  getToken,
  syncEnabled = false,
}: ThemePreferenceOptions) {
  const [preference, setPreferenceState] = useState<ThemePreference>(() =>
    storedPreference(accountId),
  );
  const [resolvedSystemTheme, setResolvedSystemTheme] = useState<Theme>(systemTheme);
  const mutationVersionRef = useRef(0);
  const writeQueueRef = useRef(Promise.resolve());

  useEffect(() => {
    mutationVersionRef.current = 0;
    writeQueueRef.current = Promise.resolve();
    setPreferenceState(storedPreference(accountId));
  }, [accountId]);

  useEffect(() => {
    if (accountId) {
      persistDevicePreference(preference);
    }
  }, [accountId, preference]);

  useEffect(() => {
    if (!accountId || !syncEnabled || !getToken) return;

    let cancelled = false;
    const startingMutationVersion = mutationVersionRef.current;
    const cachedPreference = storedPreference(accountId);
    void (async () => {
      try {
        const token = await getToken();
        const response = await fetch(`${BACKEND_API_BASE}${API_PATHS.profile}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) {
          throw new Error(`Profile read failed with status ${response.status}`);
        }
        const profile = (await response.json()) as UserProfileResponse;
        const serverPreference = profile.preferences.theme;
        if (
          cancelled ||
          mutationVersionRef.current !== startingMutationVersion ||
          !isThemePreference(serverPreference)
        ) {
          return;
        }
        if (profile.updatedAt === null) {
          const seedBody: UpdateUserProfileRequest = {
            preferences: { theme: cachedPreference },
          };
          const seedResponse = await fetch(`${BACKEND_API_BASE}${API_PATHS.profile}`, {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(seedBody),
          });
          if (!seedResponse.ok) {
            throw new Error(`Profile seed failed with status ${seedResponse.status}`);
          }
          return;
        }
        setPreferenceState(serverPreference);
        window.localStorage.setItem(themePreferenceStorageKey(accountId), serverPreference);
        persistDevicePreference(serverPreference);
      } catch (error) {
        console.error("Unable to synchronize theme preference from the user profile", error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [accountId, getToken, syncEnabled]);

  useEffect(() => {
    if (preference !== "system" || !window.matchMedia) return;

    const mediaQuery = window.matchMedia(SYSTEM_THEME_QUERY);
    const updateSystemTheme = (event: MediaQueryListEvent) => {
      setResolvedSystemTheme(event.matches ? "dark" : "light");
    };

    setResolvedSystemTheme(mediaQuery.matches ? "dark" : "light");
    mediaQuery.addEventListener("change", updateSystemTheme);
    return () => mediaQuery.removeEventListener("change", updateSystemTheme);
  }, [preference]);

  const setPreference = useCallback(
    (nextPreference: ThemePreference) => {
      mutationVersionRef.current += 1;
      setPreferenceState(nextPreference);
      if (accountId) {
        window.localStorage.setItem(themePreferenceStorageKey(accountId), nextPreference);
      }
      persistDevicePreference(nextPreference);
      if (accountId && syncEnabled && getToken) {
        const body: UpdateUserProfileRequest = {
          preferences: { theme: nextPreference },
        };
        writeQueueRef.current = writeQueueRef.current
          .catch(() => undefined)
          .then(async () => {
            const token = await getToken();
            const response = await fetch(`${BACKEND_API_BASE}${API_PATHS.profile}`, {
              method: "PUT",
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify(body),
            });
            if (!response.ok) {
              throw new Error(`Profile write failed with status ${response.status}`);
            }
          })
          .catch((error: unknown) => {
            console.error("Unable to save theme preference to the user profile", error);
          });
      }
    },
    [accountId, getToken, syncEnabled],
  );

  const resolvedTheme = preference === "system" ? resolvedSystemTheme : preference;

  useEffect(() => {
    applyThemeTokens(resolvedTheme, document.documentElement);
  }, [resolvedTheme]);

  return {
    preference,
    resolvedTheme,
    setPreference,
  };
}
